import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Pin, Plus, Search as MagIcon, ShieldAlert, Loader2, Calendar, MapPin, Hash, ThumbsUp, ThumbsDown, TriangleAlert, X, Send, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// -----------------------------------------------------------------------------
// Public Notice Board — every user sees pinned + fresh notices across a small
// taxonomy: patent, legal, traffic, odd_even, vehicle_ban, yojna, disaster,
// health, tender, b2b, general. Anyone signed in can post one. Officials can
// pin. Category filter, city filter and full-text search are wired into the
// same GET /api/notices call.
// -----------------------------------------------------------------------------
const CATEGORIES = [
  { k: "",            l: "All" },
  { k: "patent",      l: "Patent · IP" },
  { k: "legal",       l: "Legal · Cease & Desist" },
  { k: "yojna",       l: "Govt Yojna" },
  { k: "traffic",     l: "Traffic Diversion" },
  { k: "odd_even",    l: "Odd / Even" },
  { k: "vehicle_ban", l: "Vehicle Ban (BS-III etc.)" },
  { k: "disaster",    l: "Disaster / Alert" },
  { k: "health",      l: "Health" },
  { k: "tender",      l: "Tender · B2B" },
  { k: "b2b",         l: "B2B" },
  { k: "general",     l: "General" },
];

const CAT_STYLE = {
  patent:      "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
  legal:       "bg-red-100 text-red-800 border-red-300",
  yojna:       "bg-emerald-100 text-emerald-800 border-emerald-300",
  traffic:     "bg-amber-100 text-amber-800 border-amber-300",
  odd_even:    "bg-amber-100 text-amber-800 border-amber-300",
  vehicle_ban: "bg-orange-100 text-orange-800 border-orange-300",
  disaster:    "bg-red-100 text-red-900 border-red-400",
  health:      "bg-sky-100 text-sky-800 border-sky-300",
  tender:      "bg-stone-100 text-stone-800 border-stone-300",
  b2b:         "bg-indigo-100 text-indigo-800 border-indigo-300",
  general:     "bg-stone-100 text-stone-700 border-stone-300",
};

const PRIO_STYLE = {
  critical: "bg-red-600 text-white",
  high:     "bg-amber-500 text-white",
  medium:   "bg-stone-500 text-white",
  low:      "bg-stone-300 text-stone-800",
};

export default function Notices() {
  const { user } = useAuth();
  const [category, setCategory] = useState("");
  const [term, setTerm] = useState("");
  const [city, setCity] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useSeo({
    title: "Public Notice Board · find2near",
    description: "Nation-wide notice board for patents, govt yojna, traffic diversions, odd/even, BS-III bans and B2B alerts. Long-term and short-term notices from every user.",
    canonical: "/notices",
  });

  const load = () => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (term)     params.term = term;
    if (city)     params.city = city;
    if (q)        params.q = q;
    api.get("/notices", { params })
       .then((r) => setItems(r.data))
       .catch(() => setItems([]))
       .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [category, term, city]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitSearch = (e) => { e.preventDefault(); load(); };

  const pinned = useMemo(() => items.filter((x) => x.pinned), [items]);
  const rest   = useMemo(() => items.filter((x) => !x.pinned), [items]);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6" data-testid="notices-page">
      <NoticeForm open={showForm} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />

      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2">
            <Megaphone size={12} className="text-[#E05E36]" /> Public notice board
          </div>
          <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Every citizen. <span className="text-[#E05E36]">Every notice.</span></h1>
          <p className="text-stone-500 text-sm mt-1">Patents · Legal · Traffic diversions · New yojna · Odd/Even · BS-III bans · Disaster alerts.</p>
        </div>
        {user ? (
          <Button onClick={() => setShowForm(true)} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] h-10 px-5 font-black" data-testid="notice-new-btn">
            <Plus size={16} className="mr-1.5" /> New notice
          </Button>
        ) : (
          <Link to="/onboarding" className="rounded-full bg-[#E05E36] text-white px-5 h-10 flex items-center gap-1 font-black">
            Sign in to post
          </Link>
        )}
      </div>

      {/* Filters + search */}
      <form onSubmit={submitSearch} className="bg-white border border-stone-200 rounded-3xl p-3 flex flex-wrap gap-2 items-center mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] bg-stone-50 rounded-2xl px-3 py-1.5">
          <MagIcon size={14} className="text-stone-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onBlur={load}
                 placeholder="Search title, body, #hashtags…"
                 className="bg-transparent w-full outline-none text-sm" data-testid="notice-search" />
        </div>
        <Select value={category || "__all__"} onValueChange={(v) => setCategory(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-full w-56 h-9 text-xs" data-testid="notice-cat"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => <SelectItem key={c.k || "__all__"} value={c.k || "__all__"}>{c.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={term || "__any__"} onValueChange={(v) => setTerm(v === "__any__" ? "" : v)}>
          <SelectTrigger className="rounded-full w-36 h-9 text-xs" data-testid="notice-term"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">Any term</SelectItem>
            <SelectItem value="long">Long term</SelectItem>
            <SelectItem value="short">Short term</SelectItem>
          </SelectContent>
        </Select>
        <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" className="rounded-full h-9 w-36 text-xs" data-testid="notice-city" />
      </form>

      {/* Results */}
      {loading ? (
        <div className="text-center py-16 text-stone-400"><Loader2 className="mx-auto animate-spin mb-2" /> Loading notices…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-stone-400 border border-dashed border-stone-200 rounded-3xl">
          No notices yet. {user ? "Post the first one!" : "Sign in to post the first one!"}
        </div>
      ) : (
        <div className="space-y-6">
          {pinned.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-amber-700 flex items-center gap-2 mb-2">
                <Pin size={12} /> Pinned by officials
              </div>
              <div className="space-y-3">{pinned.map((n) => <NoticeCard key={n.notice_id} n={n} onChange={load} />)}</div>
            </div>
          )}
          <div>
            {pinned.length > 0 && <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2">Latest</div>}
            <div className="space-y-3">{rest.map((n) => <NoticeCard key={n.notice_id} n={n} onChange={load} />)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function NoticeCard({ n, onChange }) {
  const { user } = useAuth();
  const catStyle = CAT_STYLE[n.category] || CAT_STYLE.general;
  const prioStyle = PRIO_STYLE[n.priority] || PRIO_STYLE.medium;
  const isLegalLike = n.category === "legal" || n.category === "patent" || n.category === "disaster";
  const isMine = user && n.author_id === user.user_id;

  const react = async (kind) => {
    try { await api.post(`/notices/${n.notice_id}/react`, { kind }); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this notice?")) return;
    try { await api.delete(`/notices/${n.notice_id}`); toast.success("Deleted"); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const pin = async () => {
    try { await api.post(`/notices/${n.notice_id}/pin`, { pinned: !n.pinned }); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Officials only"); }
  };

  return (
    <article className={`rounded-3xl bg-white border p-5 ${isLegalLike ? "border-red-300" : "border-stone-200"}`} data-testid={`notice-card-${n.notice_id}`}>
      {isLegalLike && (
        <div className="mb-2 -mt-1 -mx-1 text-[10px] font-black uppercase tracking-widest text-white bg-red-600 rounded-full px-3 py-1 inline-flex items-center gap-1">
          <ShieldAlert size={11} /> {n.category === "patent" ? "IP / Patent notice" : n.category === "legal" ? "Legal notice" : "Disaster alert"}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <span className={`text-[10px] font-black uppercase tracking-widest border rounded-full px-2 py-0.5 ${catStyle}`}>{n.category.replace(/_/g, " ")}</span>
        <span className={`text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 ${prioStyle}`}>{n.priority}</span>
        {n.term === "short" && <span className="text-[10px] font-black uppercase tracking-widest border rounded-full px-2 py-0.5 bg-yellow-100 text-yellow-800 border-yellow-300">short term</span>}
        {n.reference_no && <span className="text-[10px] font-mono text-stone-500">· {n.reference_no}</span>}
      </div>
      <h2 className="heading text-lg md:text-xl font-black text-stone-900 leading-tight">{n.title}</h2>
      <div className="text-[11px] text-stone-500 flex flex-wrap items-center gap-2 mt-0.5">
        <span>by <b className="text-stone-700">{n.author_name}</b></span>
        {n.author_is_official && <span className="inline-flex items-center gap-1 text-emerald-700 font-bold"><ShieldAlert size={10} /> Official</span>}
        {n.city && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {n.city}</span>}
        {n.expires_at && <span className="inline-flex items-center gap-1"><Calendar size={10} /> Until {n.expires_at.slice(0, 10)}</span>}
      </div>
      <p className="text-sm text-stone-700 mt-2 whitespace-pre-line leading-relaxed">{n.body}</p>
      {n.hashtags?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {n.hashtags.map((h) => (
            <span key={h} className="text-[10px] font-bold text-stone-600 bg-stone-100 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><Hash size={9} /> {h}</span>
          ))}
        </div>
      )}
      {(n.contact_name || n.contact_phone) && (
        <div className="mt-3 text-xs text-stone-600 border-t border-stone-100 pt-2 flex flex-wrap gap-x-4">
          {n.contact_name && <span>Contact: <b>{n.contact_name}</b></span>}
          {n.contact_phone && (
            <a href={`tel:${n.contact_phone}`} className="text-emerald-700 hover:underline inline-flex items-center gap-1">
              <Phone size={11} /> {n.contact_phone}
            </a>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button onClick={() => react("up")}   className="text-xs font-bold text-emerald-700 hover:bg-emerald-50 rounded-full px-2 py-1 inline-flex items-center gap-1"   data-testid={`react-up-${n.notice_id}`}><ThumbsUp size={12} /> {n.reactions?.up || 0}</button>
        <button onClick={() => react("down")} className="text-xs font-bold text-stone-600 hover:bg-stone-50  rounded-full px-2 py-1 inline-flex items-center gap-1"   data-testid={`react-down-${n.notice_id}`}><ThumbsDown size={12} /> {n.reactions?.down || 0}</button>
        <button onClick={() => react("warn")} className="text-xs font-bold text-amber-700 hover:bg-amber-50 rounded-full px-2 py-1 inline-flex items-center gap-1"     data-testid={`react-warn-${n.notice_id}`}><TriangleAlert size={12} /> {n.reactions?.warn || 0}</button>
        <span className="text-[10px] text-stone-400 ml-auto">{n.views || 0} views</span>
        {user && <button onClick={pin} className="text-[11px] font-bold text-amber-800 hover:bg-amber-50 rounded-full px-2 py-1 inline-flex items-center gap-1" data-testid={`pin-${n.notice_id}`}><Pin size={11} /> {n.pinned ? "Unpin" : "Pin"}</button>}
        {isMine && <button onClick={remove} className="text-[11px] font-bold text-red-700 hover:bg-red-50 rounded-full px-2 py-1" data-testid={`del-${n.notice_id}`}>Delete</button>}
      </div>
    </article>
  );
}

// -----------------------------------------------------------------------------
const emptyNotice = () => ({
  title: "", body: "",
  category: "general", term: "long",
  priority: "medium",
  expires_at: "", city: "", country: "",
  hashtags: "", reference_no: "",
  contact_name: "", contact_phone: "",
});

function NoticeForm({ open, onClose, onSaved }) {
  const [f, setF] = useState(emptyNotice());
  const [busy, setBusy] = useState(false);

  if (!open) return null;
  const set = (patch) => setF((s) => ({ ...s, ...patch }));

  const submit = async () => {
    if (f.title.trim().length < 3 || f.body.trim().length < 3) {
      toast.error("Title and body are required (min 3 chars)");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...f,
        expires_at: f.term === "short" ? (f.expires_at || null) : null,
        hashtags: f.hashtags.split(/[,\s]+/).filter(Boolean),
      };
      await api.post("/notices", payload);
      toast.success("Notice published");
      setF(emptyNotice());
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to publish");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="notice-form-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-gradient-to-r from-[#E05E36]/10 to-white">
          <div className="flex items-center gap-2"><Megaphone className="text-[#E05E36]" size={16} /><b>Publish public notice</b></div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="notice-form-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-2 overflow-y-auto">
          <Input value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder="Title (e.g. B2B WARNING & AWARENESS REGARDING CEASE & DESIST)" className="rounded-2xl h-11 text-base font-bold" data-testid="notice-title" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.reference_no} onChange={(e) => set({ reference_no: e.target.value })} placeholder="Reference no. (e.g. NO. 025/2026)" className="rounded-2xl h-10 font-mono" data-testid="notice-ref" />
            <Select value={f.priority} onValueChange={(v) => set({ priority: v })}>
              <SelectTrigger className="rounded-2xl h-10" data-testid="notice-prio"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["low", "medium", "high", "critical"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={f.category} onValueChange={(v) => set({ category: v })}>
              <SelectTrigger className="rounded-2xl h-10" data-testid="notice-form-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.filter((c) => c.k).map((c) => <SelectItem key={c.k} value={c.k}>{c.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={f.term} onValueChange={(v) => set({ term: v })}>
              <SelectTrigger className="rounded-2xl h-10" data-testid="notice-form-term"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="long">Long term (no time bound)</SelectItem>
                <SelectItem value="short">Short term (has expiry)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {f.term === "short" && (
            <Input value={f.expires_at} onChange={(e) => set({ expires_at: e.target.value })}
                   placeholder="Expires at (YYYY-MM-DD or full ISO)" className="rounded-2xl h-10 font-mono" data-testid="notice-expiry" />
          )}
          <textarea value={f.body} onChange={(e) => set({ body: e.target.value })} rows={8}
                    placeholder="Body — paste your full notice text here."
                    className="w-full px-4 py-3 rounded-2xl border border-stone-200 outline-none text-sm leading-relaxed"
                    data-testid="notice-body" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.city} onChange={(e) => set({ city: e.target.value })} placeholder="City (blank = global)" className="rounded-2xl h-10" data-testid="notice-form-city" />
            <Input value={f.country} onChange={(e) => set({ country: e.target.value })} placeholder="Country" className="rounded-2xl h-10" data-testid="notice-form-country" />
          </div>
          <Input value={f.hashtags} onChange={(e) => set({ hashtags: e.target.value })}
                 placeholder="Hashtags (comma or space separated) e.g. #ROYALTY_FEES, #TRADEMARK_PERMISSION"
                 className="rounded-2xl h-10 font-mono text-xs" data-testid="notice-hashtags" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.contact_name} onChange={(e) => set({ contact_name: e.target.value })} placeholder="Contact name (e.g. PUSHKAR AGARWAL)" className="rounded-2xl h-10" data-testid="notice-cname" />
            <Input value={f.contact_phone} onChange={(e) => set({ contact_phone: e.target.value })} placeholder="Contact phone" className="rounded-2xl h-10 font-mono" data-testid="notice-cphone" />
          </div>
        </div>
        <div className="border-t border-stone-200 p-4 flex items-center justify-between">
          <div className="text-[10px] text-stone-500">Legal / patent notices auto-render with a red banner across the board.</div>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] font-black" data-testid="notice-publish">
            {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <Send size={14} className="mr-2" />}
            Publish
          </Button>
        </div>
      </div>
    </div>
  );
}
